import { useCallback, useState } from "react"
import { useNavigate } from "react-router-dom"
import { useQueryClient } from "@tanstack/react-query"

import { apiClient } from "@/lib/api-client"
import { clearStoredToken, getSession, storeToken } from "@/lib/auth-token"
import type { LoginRequest, LoginResponse } from "@/lib/types"

export function useLogin() {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const login = useCallback(async (credentials: LoginRequest) => {
    setIsLoading(true)
    setError(null)
    try {
      const response = await apiClient.post<LoginResponse>("/auth/login", credentials)
      const token = response.data?.token
      if (!token) throw new Error("Login response did not include an access token.")
      storeToken(token)
      return token
    } catch (err) {
      const message = err instanceof Error ? err.message : "Login failed"
      setError(message)
      throw err
    } finally {
      setIsLoading(false)
    }
  }, [])

  return { login, isLoading, error }
}

/** Signs out: drops the token and every cached query, then returns to the login page. */
export function useLogout() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  return useCallback(() => {
    clearStoredToken()
    queryClient.clear()
    navigate("/", { replace: true })
  }, [navigate, queryClient])
}

export function useSession() {
  return getSession()
}
